import { AUTHORS } from './authors';
import { PageMeta } from 'constants/pages';
import { BOOK_A_DEMO_LINK } from 'constants/common';
import { EHashtag, TCardProps } from 'types/common';
import { ArticleSections } from 'types/articles';

import HeaderImage from 'assets/resources/ai-live-sim-glossary/core-simulation-concepts.webp';
import SyntheticDataImage from 'assets/resources/ai-live-sim-glossary/synthetic-data-and-domain-techniques.webp';
import SensorsImage from 'assets/resources/ai-live-sim-glossary/sensors-and-perception-simulation.webp';
import AiMlAutonomyImage from 'assets/resources/ai-live-sim-glossary/ai-machine-learning-and-autonomy.webp';
import SafetyImage from 'assets/resources/ai-live-sim-glossary/safety-reliability-and-certification.webp';
import TestInfraImage from 'assets/resources/ai-live-sim-glossary/test-infrastructure-and-developmen-workflow.webp';
import MaritimeDefenseImage from 'assets/resources/ai-live-sim-glossary/maritime-and-defense-specific-terms.webp';

const commonData = {
  ...AUTHORS.AILiveSim,
  title: 'AILiveSim Glossary',
  publishDate: 'March 7, 2026',
  minutesOfRead: 12,
};

export const seventhMarch2026ArticleMeta: PageMeta = {
  url: '/articles/ailivesim-glossary',
  title: commonData.title,
  description:
    'A practical reference to clarify the key concepts shaping modern autonomous system testing, from multi-sensor simulation to safety validation and sim-to-real transfer.',
};

export const seventhMarch2026ArticleData: ArticleSections[] = [
  {
    resourceType: 'Article',
    image: HeaderImage,
    imageCaption: 'Core Simulation Concepts',
    type: 'header',
    linkedInPostId: 'https://www.linkedin.com/company/ailivesim/',
    text: (
      <>
        <p>
          Simulation, synthetic data, sensor modeling, autonomy validation and the terminology
          around AI-driven development can quickly become complex.
        </p>
        <p>
          We&apos;ve created the AILiveSim Glossary as a practical reference to clarify the key
          concepts shaping modern autonomous system testing, from multi-sensor simulation to safety
          validation and sim-to-real transfer.
        </p>
        <p>
          Whether you&apos;re new to the field or deep in development, this guide helps align
          language with technology.
        </p>
      </>
    ),
    ...commonData,
  },

  {
    type: 'common',
    title: '1. Core Simulation Concepts',
    text: (
      <>
        <p>
          <strong>AI-Enriched Simulation</strong>
          <br />
          Simulation enhanced with AI to generate dynamic, realistic environments for autonomous
          system testing.{' '}
          <a href="/articles/synthetic-environments-transform-training">Learn more &rarr;</a>
        </p>
        <p>
          <strong>Physics-Based Simulation</strong>
          <br />
          Simulation that follows physical laws (gravity, aerodynamics, collisions, fluids) for
          realism.{' '}
          <a href="/articles/physics-based-synthetic-data">Learn more &rarr;</a>
        </p>
        <p>
          <strong>Real-Time Simulation</strong>
          <br />
          Simulation that runs at real-world speed, enabling live interaction or hardware
          integration.{' '}
          <a href="/automated-data-pipelines">Learn more &rarr;</a>
        </p>
        <p>
          <strong>Closed-Loop Simulation</strong>
          <br />
          Testing where the system&apos;s decisions influence the environment, creating a feedback
          loop.{' '}
          <a href="/articles/synthetic-environments-transform-training">Learn more &rarr;</a>
        </p>
        <p>
          <strong>Scenario Generation</strong>
          <br />
          Automatic or manual creation of test scenarios (weather, obstacles, behaviors).{' '}
          <a href="/articles/scenario-plugins-simulation-framework">Learn more &rarr;</a>
        </p>
        <p>
          <strong>Synthetic Environment</strong>
          <br />
          A fully virtual world representing real terrain, objects, and environmental conditions.{' '}
          <a href="/whitepapers/using-synthetic-data-from-ailivesim">Learn more &rarr;</a>
        </p>
        <p>
          <strong>Digital Twin</strong>
          <br />
          Virtual replica of a real-world asset or system for simulation and validation.{' '}
          <a href="/customer-case/autonomous-aircraft-taxiing">Learn more &rarr;</a>
        </p>
        <p>
          <strong>Ground Truth Data</strong>
          <br />
          Perfectly accurate labels (object position, classification, distance) generated in
          simulation.{' '}
          <a href="/articles/synthetic-environments-transform-training">Learn more &rarr;</a>
        </p>
        <p>
          <strong>Scenario Replay</strong>
          <br />
          Recording and reproducing simulation runs for debugging or validation.{' '}
          <a href="/articles/scenario-plugins-simulation-framework">Learn more &rarr;</a>
        </p>
        <p>
          <strong>Simulation Asset Library</strong>
          <br />
          Collection of reusable 3D models, environments, and scenarios.{' '}
          <a href="/all-industries">Learn more &rarr;</a>
        </p>
      </>
    ),
  },

  {
    type: 'common',
    title: '2. Synthetic Data & Domain Techniques',
    image: SyntheticDataImage,
    text: (
      <>
        <p>
          <strong>Synthetic Data</strong>
          <br />
          Artificially generated data (images, LiDAR, radar, etc.) for training and testing AI.{' '}
          <a href="/whitepapers/using-synthetic-data-from-ailivesim">Learn more &rarr;</a>
        </p>
        <p>
          <strong>Synthetic Sensor Data Generation</strong>
          <br />
          Creation of sensor-specific outputs (point clouds, radar returns, thermal images).{' '}
          <a href="/articles/thermal-camera-sensors-ailivesim-platform">Learn more &rarr;</a>
        </p>
        <p>
          <strong>Synthetic Data Augmentation</strong>
          <br />
          Enhancing training datasets with simulation-generated variations.{' '}
          <a href="/articles/data-augmentation-methods">Learn more &rarr;</a>
        </p>
        <p>
          <strong>Domain Randomization</strong>
          <br />
          Systematic variation of textures, lighting, weather, and objects to improve model
          robustness.{' '}
          <a href="/platform">Learn more &rarr;</a>
        </p>
        <p>
          <strong>Sim-to-Real Transfer</strong>
          <br />
          Ability of models validated in simulation to perform reliably in the real world.{' '}
          <a href="/whitepapers/using-synthetic-data-from-ailivesim">Learn more &rarr;</a>
        </p>
      </>
    ),
  },

  {
    type: 'common',
    title: '3. Sensors & Perception Simulation',
    image: SensorsImage,
    text: (
      <>
        <p>
          <strong>Sensor Simulation</strong>
          <br />
          Reproducing outputs from cameras, LiDAR, radar, sonar, thermal sensors, etc.{' '}
          <a href="/sensors">Learn more &rarr;</a>
        </p>
        <p>
          <strong>Multi-Sensor Fusion</strong>
          <br />
          Combining multiple sensor inputs to improve perception accuracy.{' '}
          <a href="/sensors">Learn more &rarr;</a>
        </p>
        <p>
          <strong>Ray Tracing</strong>
          <br />
          High-fidelity light/path simulation used for realistic camera and LiDAR rendering.{' '}
          <a href="/sensors">Learn more &rarr;</a>
        </p>
        <p>
          <strong>LiDAR Simulation</strong>
          <br />
          Generation of 3D point clouds with realistic noise and reflectivity.{' '}
          <a href="/sensors">Learn more &rarr;</a>
        </p>
        <p>
          <strong>Radar Simulation</strong>
          <br />
          Emulation of radar returns including range, velocity, and material properties.{' '}
          <a href="/sensors">Learn more &rarr;</a>
        </p>
        <p>
          <strong>Thermal / Infrared Simulation</strong>
          <br />
          Simulating heat signatures for IR-based perception systems.{' '}
          <a href="/sensors">Learn more &rarr;</a>
        </p>
        <p>
          <strong>Sensor Noise Modeling</strong>
          <br />
          Simulating imperfections such as blur, occlusion, and reflections.{' '}
          <a href="/sensors">Learn more &rarr;</a>
        </p>
        <p>
          <strong>Sonar Simulation (Maritime/Defense)</strong>
          <br />
          Underwater acoustic sensor modeling.{' '}
          <a href="/sensors">Learn more &rarr;</a>
        </p>
      </>
    ),
  },

  {
    type: 'common',
    title: '4. AI, Machine Learning & Autonomy',
    image: AiMlAutonomyImage,
    text: (
      <>
        <p>
          <strong>Perception Algorithm</strong>
          <br />
          AI models that detect, classify, and track objects from sensor data.{' '}
          <a href="/articles/synthetic-environments-transform-training">Learn more &rarr;</a>
        </p>
        <p>
          <strong>Model Validation</strong>
          <br />
          Testing AI models under diverse simulated conditions to assess accuracy and robustness.{' '}
          <a href="/applications/automatic-dataset-enhancement">Learn more &rarr;</a>
        </p>
        <p>
          <strong>Autonomous System Validation</strong>
          <br />
          Full-system testing of perception, planning, and control across the ODD.{' '}
          <a href="/platform">Learn more &rarr;</a>
        </p>
        <p>
          <strong>Path Planning</strong>
          <br />
          Algorithmic calculation of safe and efficient navigation paths.{' '}
          <a href="/articles/path-planning-for-autonomous-ships">Learn more &rarr;</a>
        </p>
        <p>
          <strong>Control Algorithm</strong>
          <br />
          Software translating navigation decisions into motion (steering, throttle, braking).{' '}
          <a href="/articles/collision-avoidance-algorithms">Learn more &rarr;</a>
        </p>
        <p>
          <strong>Operational Design Domain (ODD)</strong>
          <br />
          All environmental and operational conditions intended for autonomous system use.{' '}
          <a href="/articles/auto-docking-simulation-maritime">Learn more &rarr;</a>
        </p>
        <p>
          <strong>Edge Case</strong>
          <br />
          Rare or extreme events difficult to test in real life but easily simulated.{' '}
          <a href="/applications/automatic-dataset-enhancement">Learn more &rarr;</a>
        </p>
      </>
    ),
  },

  {
    type: 'common',
    title: '5. Safety, Reliability & Certification',
    image: SafetyImage,
    text: (
      <>
        <p>
          <strong>Safety Validation</strong>
          <br />
          Ensuring systems behave safely across all scenarios, including worst-case events.{' '}
          <a href="/customer-case/anti-collision-systems">Learn more &rarr;</a>
        </p>
        <p>
          <strong>Failure Mode Testing</strong>
          <br />
          Evaluating system behavior during sensor faults or unexpected failures.{' '}
          <a href="/technical-briefs/classical-simulation-testing-doesnt-scale">
            Learn more &rarr;
          </a>
        </p>
        <p>
          <strong>Coverage Analysis</strong>
          <br />
          Measuring what percentage of the ODD has been tested in simulation.{' '}
          <a href="/articles/auto-docking-simulation-maritime">Learn more &rarr;</a>
        </p>
        <p>
          <strong>ISO 26262 / Safety Standards</strong>
          <br />
          Functional safety frameworks addressed using simulation.{' '}
          <a href="/articles/autonomous-aircraft-taxiing">Learn more &rarr;</a>
        </p>
      </>
    ),
  },

  {
    type: 'common',
    title: '6. Test Infrastructure & Development Workflow',
    image: TestInfraImage,
    text: (
      <>
        <p>
          <strong>Simulation-in-the-Loop (SiL)</strong> is a closed-loop approach in which the
          simulator acts as the primary orchestrator&mdash;providing the operational context and
          inputs that drive end-to-end development and validation.{' '}
          <a href="/articles/simulation-in-the-loop-sil-save-time-money">Learn more &rarr;</a>
        </p>
        <p>
          <strong>Model-in-the-Loop (MiL)</strong> operates at the earliest stage of development,
          where both the controller and the physical plant are represented as abstract models, with
          no compiled code or real hardware present, useful for rapid algorithm prototyping, but
          limited in fidelity.{' '}
          <a href="/articles/model-in-the-loop-simulation">Learn more &rarr;</a>
        </p>
        <p>
          <strong>Software-in-the-Loop</strong> advances one step further by replacing the abstract
          model with actual compiled code running against a simulated environment; its primary
          purpose is verifying model-code equivalence and catching software regressions before
          hardware enters the picture.{' '}
          <a href="/">Learn more &rarr;</a>
        </p>
        <p>
          <strong>Hardware-in-the-Loop (HiL)</strong> connects real physical computing hardware, such
          as ECUs or embedded processors, directly to the simulation, validating real-time timing,
          signal integrity, and hardware-software interactions that neither MiL nor SWiL can
          replicate.{' '}
          <a href="/">Learn more &rarr;</a>
        </p>
        <p>
          <strong>Human-on-the-Loop</strong> introduces a human operator as an additional oversight
          layer, the human is not the system under test, but an external decision-maker who interacts
          with the loop to assess usability, safety, and edge-case judgment.{' '}
          <a href="/">Learn more &rarr;</a>
        </p>
        <p>
          <strong>Continuous Integration / Continuous Development (CI/CD)</strong>
          <br />
          Automated simulation tests that run whenever code changes, ensuring consistent validation.{' '}
          <a href="/all-applications">Learn more &rarr;</a>
        </p>
        <p>
          <strong>Parallelization</strong>
          <br />
          Running multiple simulations simultaneously to accelerate testing.
        </p>
      </>
    ),
  },

  {
    type: 'common',
    title: '7. Maritime & Defense-Specific Terms',
    image: MaritimeDefenseImage,
    text: (
      <>
        <p>
          <strong>Sea State Simulation</strong>
          <br />
          Modeling waves, currents, and weather for maritime autonomy.{' '}
          <a href="/articles/virtual-sea-trials-what-simulation-testing-reveals-about-maritime-surveillance-performance">
            Learn more &rarr;
          </a>
        </p>
        <p>
          <strong>Battlefield/Defense Simulation (general)</strong>
          <br />
          Use of simulation for mission-critical AI testing such as detection, navigation, and
          threat avoidance.{' '}
          <a href="/Industries/navy-defence">Learn more &rarr;</a>
        </p>
      </>
    ),
  },

  {
    type: 'common',
    title: 'Ready to Take the Next Step?',
    text: (
      <>
        <p>
          Our simulation platform helps teams building autonomous systems generate sensor data,
          build training and validation datasets, and stress-test across edge cases, all within
          physics-validated 3D environments.
        </p>
        <p>
          If you&apos;d like to see how this works in practice, we&apos;d be happy to walk you
          through it.
        </p>
        <p>
          <strong>
            <a href={BOOK_A_DEMO_LINK} target="_blank" rel="noopener noreferrer">
              Book Your Demo Today
            </a>
          </strong>
        </p>
        <p>
          <strong>
            <a href="/resources">
              Subscribe to our newsletter
            </a>
          </strong>
        </p>
      </>
    ),
  },
];

export const seventhMarch2026ArticleCard: TCardProps = {
  description:
    'A practical glossary covering simulation, synthetic data, sensor modeling, autonomy validation and the key terminology around AI-driven autonomous system testing.',
  image: HeaderImage,
  type: 'Article',
  category: 'articles',
  postUrl: seventhMarch2026ArticleMeta.url,
  ...commonData,
  hashTags: [
    EHashtag.SyntheticData,
    EHashtag.AISimulation,
    EHashtag.ComputerVision,
    EHashtag.SafetyValidation,
    EHashtag.DefenseTechnology,
    EHashtag.MaritimeTechnology,
  ],
};
